// frontend/src/utils/timeUtils.js
// 통합 시간 관리 유틸리티

export class TimeScheduleManager {
  constructor() {
    this.dayMapping = {
      'sun': { ko: '일', full: '일요일', index: 0 },
      'mon': { ko: '월', full: '월요일', index: 1 },
      'tue': { ko: '화', full: '화요일', index: 2 },
      'wed': { ko: '수', full: '수요일', index: 3 },
      'thu': { ko: '목', full: '목요일', index: 4 },
      'fri': { ko: '금', full: '금요일', index: 5 },
      'sat': { ko: '토', full: '토요일', index: 6 }
    };
    
    // 영어 요일 매핑
    this.englishDayMapping = {
      'sunday': 'sun', 'sun': 'sun',
      'monday': 'mon', 'mon': 'mon',
      'tuesday': 'tue', 'tue': 'tue',
      'wednesday': 'wed', 'wed': 'wed',
      'thursday': 'thu', 'thu': 'thu',
      'friday': 'fri', 'fri': 'fri',
      'saturday': 'sat', 'sat': 'sat',
      'everyday': 'everyday', 'daily': 'everyday'
    };
    
    // 특수 시간 표현 매핑
    this.specialTimeMapping = {
      'late': '09:00',
      'early': '06:00',
      'midnight': '00:00',
      'noon': '12:00'
    };
    
    this.reverseDayMapping = Object.fromEntries(
      Object.entries(this.dayMapping).map(([key, val]) => [val.full, key])
    );
  }

  // === 포맷 변환 메서드들 ===
  
  /**
   * 기존 문자열 포맷을 JSON으로 변환
   * "월요일: 22:00 ~ 06:00\n화요일: 22:00 ~ 06:00" -> JSON
   */
  parseStringToJSON(operatingHoursString) {
    if (!operatingHoursString) return null;
    
    const schedule = [];
    const lines = operatingHoursString.split('\n');
    const timeGroups = new Map();
    
    lines.forEach(line => {
      const parts = line.split(':');
      if (parts.length < 2) return;
      
      const dayLabel = parts[0].trim();
      const timePart = parts.slice(1).join(':').trim();
      const dayKey = this.reverseDayMapping[dayLabel];
      
      if (!dayKey) return;
      
      if (timePart === '휴무') {
        // 휴무일 처리
        const existingClosed = timeGroups.get('closed') || [];
        existingClosed.push(dayKey);
        timeGroups.set('closed', existingClosed);
      } else {
        // 시간 포맷 매칭: 시간:분 ~ 시간:분 또는 시간:분 ~ late 등 특수 문자
        const timeMatch = timePart.match(/(\d{2}:\d{2})\s*~\s*(\d{2}:\d{2}|late|early|midnight|noon)/);
        if (timeMatch) {
          const [, startTime, endTime] = timeMatch;
          const timeKey = `${startTime}-${endTime}`;
          const existingDays = timeGroups.get(timeKey) || [];
          existingDays.push(dayKey);
          timeGroups.set(timeKey, existingDays);
        }
      }
    });
    
    // JSON 스케줄 생성
    timeGroups.forEach((days, timeKey) => {
      if (timeKey === 'closed') {
        schedule.push({
          days: days,
          hours: null
        });
      } else {
        const [start, end] = timeKey.split('-');
        schedule.push({
          days: days,
          hours: { start, end }
        });
      }
    });
    
    return {
      schedule,
      timezone: "Asia/Seoul",
      last_updated: new Date().toISOString()
    };
  }
  
  /**
   * JSON을 압축된 문자열 포맷으로 변환
   * JSON -> "목/금/토 22:00-04:00\n일 휴무"
   */
  formatJSONToCompact(scheduleJSON) {
    if (!scheduleJSON?.schedule) return '';
    
    return scheduleJSON.schedule.map(item => {
      const dayLabels = item.days.map(day => this.dayMapping[day]?.ko).join('/');
      
      if (!item.hours) {
        return `${dayLabels} 휴무`;
      }
      
      return `${dayLabels} ${item.hours.start}-${item.hours.end}`;
    }).join('\n');
  }
  
  /**
   * JSON을 기존 상세 문자열 포맷으로 변환 (호환성 유지)
   * JSON -> "월요일: 22:00 ~ 06:00\n화요일: 22:00 ~ 06:00"
   */
  formatJSONToString(scheduleJSON) {
    if (!scheduleJSON?.schedule) return '';
    
    const allDays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dayToHours = new Map();
    
    // 스케줄 정보를 요일별로 매핑
    scheduleJSON.schedule.forEach(item => {
      item.days.forEach(day => {
        dayToHours.set(day, item.hours);
      });
    });
    
    // 요일 순서대로 포맷팅
    return allDays
      .filter(day => dayToHours.has(day))
      .map(day => {
        const hours = dayToHours.get(day);
        const dayLabel = this.dayMapping[day].full;
        
        if (!hours) {
          return `${dayLabel}: 휴무`;
        }
        
        return `${dayLabel}: ${hours.start} ~ ${hours.end}`;
      })
      .join('\n');
  }
  
  /**
   * 압축 포맷을 JSON으로 변환
   * "목/금/토 22:00-04:00\n일 휴무" -> JSON
   */
  parseCompactToJSON(compactString) {
    if (!compactString) return null;
    
    const schedule = [];
    const lines = compactString.split('\n');
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      if (trimmed.includes('휴무')) {
        // 휴무 처리: "월/화 휴무"
        const dayLabels = trimmed.replace('휴무', '').trim().split('/');
        const days = dayLabels
          .map(label => Object.keys(this.dayMapping).find(key => 
            this.dayMapping[key].ko === label.trim()
          ))
          .filter(Boolean);
        
        if (days.length > 0) {
          schedule.push({ days, hours: null });
        }
      } else {
        // 운영시간 처리: 여러 포맷 지원
        this.parseOperatingLine(trimmed, schedule);
      }
    });
    
    return {
      schedule,
      timezone: "Asia/Seoul",
      last_updated: new Date().toISOString()
    };
  }

  /**
   * 운영시간 라인 파싱 (복합 포맷 지원)
   * 예: "수/목 19:00-00:00 (Bar 영업만) 금/토 20:00-02:00"
   * 영어: "Everyday 21:00-late", "Mon/Tue 18:00-midnight"
   */
  parseOperatingLine(line, schedule) {
    // 괄호 제거 (부가 설명 제거)
    const cleanLine = line.replace(/\([^)]*\)/g, '').trim();
    
    // 한국어 요일이 포함되어 있는지 확인 (우선순위를 높게)
    const hasKoreanDays = /[월화수목금토일]/.test(cleanLine);
    
    // 영어 포맷인지 확인 (한국어 요일이 없는 경우에만)
    const isEnglish = !hasKoreanDays && /[a-zA-Z]/.test(cleanLine);
    
    if (isEnglish) {
      this.parseEnglishOperatingLine(cleanLine, schedule);
    } else {
      // 기존 한국어 포맷 처리
      const blocks = this.splitOperatingBlocks(cleanLine);
      
      blocks.forEach(block => {
        const trimmedBlock = block.trim();
        if (!trimmedBlock) return;
        
        // 각 블록을 "요일 시간" 형태로 파싱
        const parts = trimmedBlock.split(/\s+/);
        if (parts.length >= 2) {
          const daysPart = parts[0];
          const timePart = parts[1];
          
          // 시간 형식 확인
          if (timePart.includes('-') && (timePart.includes(':') || this.hasSpecialTimeInString(timePart))) {
            const [startTime, endTime] = timePart.split('-');
            
            // late 등 특수 시간은 원본 문자열 유지
            const processedStart = this.isSpecialTime(startTime) ? startTime : this.normalizeTime(startTime);
            const processedEnd = this.isSpecialTime(endTime) ? endTime : this.normalizeTime(endTime);
            
            const dayLabels = daysPart.split('/');
            const days = dayLabels
              .map(label => Object.keys(this.dayMapping).find(key => 
                this.dayMapping[key].ko === label.trim()
              ))
              .filter(Boolean);
            
            if (days.length > 0 && processedStart && processedEnd) {
              schedule.push({
                days,
                hours: { start: processedStart, end: processedEnd }
              });
            }
          }
        }
      });
    }
  }

  /**
   * 영어 영업시간 라인 파싱
   * 예: "Everyday 21:00-late", "Mon/Tue/Wed 18:00-midnight"
   */
  parseEnglishOperatingLine(line, schedule) {
    // 영어 패턴: "Everyday 21:00-late" 또는 "Mon/Tue 18:00-02:00"
    const englishPattern = /([a-zA-Z/]+)\s+(\d{1,2}:\d{2}-[a-zA-Z0-9:]+)/i;
    const match = line.match(englishPattern);
    
    if (match) {
      const [, daysPart, timePart] = match;
      const [startTime, endTime] = timePart.split('-');
      
      // late 등 특수 시간은 원본 문자열 유지
      const processedStart = this.isSpecialTime(startTime) ? startTime : this.normalizeTime(startTime);
      const processedEnd = this.isSpecialTime(endTime) ? endTime : this.normalizeTime(endTime);
      
      if (processedStart && processedEnd) {
        const days = this.parseEnglishDays(daysPart);
        
        if (days.length > 0) {
          schedule.push({
            days,
            hours: { start: processedStart, end: processedEnd }
          });
        }
      }
    }
  }

  /**
   * 영어 요일 파싱
   * "Everyday" -> ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
   * "Mon/Tue/Wed" -> ['mon', 'tue', 'wed']
   */
  parseEnglishDays(daysPart) {
    const daysPortion = daysPart.toLowerCase().trim();
    
    // "everyday" 또는 "daily" 처리
    if (this.englishDayMapping[daysPortion] === 'everyday') {
      return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    }
    
    // 슬래시로 구분된 요일들 처리
    const dayLabels = daysPortion.split('/');
    return dayLabels
      .map(day => this.englishDayMapping[day.trim()])
      .filter(Boolean);
  }

  /**
   * 특수 시간 표현이 포함되어 있는지 확인
   */
  hasSpecialTime(timePart) {
    const lowerTime = timePart.toLowerCase();
    return Object.keys(this.specialTimeMapping).some(special => 
      lowerTime.includes(special)
    );
  }

  /**
   * 특수 시간 표현인지 확인
   * "late", "early", "midnight", "noon" 등
   */
  isSpecialTime(timeStr) {
    if (!timeStr) return false;
    const lowerTime = timeStr.toLowerCase().trim();
    return this.specialTimeMapping.hasOwnProperty(lowerTime);
  }

  /**
   * 문자열에 특수 시간 표현이 포함되어 있는지 확인
   */
  hasSpecialTimeInString(str) {
    if (!str) return false;
    const lowerStr = str.toLowerCase();
    return Object.keys(this.specialTimeMapping).some(special => lowerStr.includes(special));
  }

  /**
   * 시간 정규화 (특수 표현을 실제 시간으로 변환)
   * "late" -> "02:00", "midnight" -> "00:00"
   * 주의: 이 메서드는 내부 계산용이며, 표시용으로는 원본 문자열을 사용해야 함
   */
  normalizeTime(timeStr) {
    if (!timeStr) return null;
    
    const lowerTime = timeStr.toLowerCase().trim();
    
    // 특수 시간 표현 확인
    for (const [special, normalized] of Object.entries(this.specialTimeMapping)) {
      if (lowerTime === special) {
        return normalized;
      }
    }
    
    // 이미 정상적인 시간 형식인지 확인 (HH:mm)
    if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
      return timeStr;
    }
    
    return null;
  }

  /**
   * 운영시간 블록 분할
   * "수/목 19:00-00:00 금/토 20:00-02:00" -> ["수/목 19:00-00:00", "금/토 20:00-02:00"]
   */
  splitOperatingBlocks(line) {
    const blocks = [];
    
    // 시간 패턴을 찾아서 블록을 분할 (특수 시간 표현 지원)
    // 끝 시간으로 숫자 형식뿐만 아니라 late, early, midnight, noon도 허용
    const timePattern = /([가-힣/]+)\s+(\d{1,2}:\d{2}-(?:\d{1,2}:\d{2}|late|early|midnight|noon))/gi;
    let match;
    
    while ((match = timePattern.exec(line)) !== null) {
      blocks.push(`${match[1]} ${match[2]}`);
    }
    
    // 패턴이 없으면 기본 분할
    if (blocks.length === 0) {
      const parts = line.split(/\s+/);
      if (parts.length >= 2) {
        blocks.push(`${parts[0]} ${parts[1]}`);
      }
    }
    
    return blocks;
  }

  /**
   * 전체 요일 영업시간 정보를 반환 (드롭다운용)
   */
  getAllDaysSchedule(scheduleData) {
    let scheduleJSON;
    
    // 입력 데이터 타입에 따른 처리
    if (typeof scheduleData === 'string') {
      if (scheduleData.includes(':') && (scheduleData.includes('~') || scheduleData.includes('-'))) {
        // 영어 포맷 확인 (Everyday, Mon, Tue 등 영어 단어 포함)
        const isEnglish = /[a-zA-Z]/.test(scheduleData);
        
        if (isEnglish || (scheduleData.includes('/') && !scheduleData.includes('요일'))) {
          // 압축 포맷 (영어 포맷 포함)
          scheduleJSON = this.parseCompactToJSON(scheduleData);
        } else {
          // 기존 한국어 문자열 포맷
          scheduleJSON = this.parseStringToJSON(scheduleData);
        }
      } else {
        return [];
      }
    } else if (typeof scheduleData === 'object' && scheduleData?.schedule) {
      scheduleJSON = scheduleData;
    } else {
      return [];
    }

    if (!scheduleJSON?.schedule) return [];

    // 모든 요일에 대한 정보 생성
    const allDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const result = [];

    allDays.forEach(dayKey => {
      const dayInfo = this.dayMapping[dayKey];
      let scheduleItem = null;
      
      // 해당 요일의 스케줄 찾기
      for (const item of scheduleJSON.schedule) {
        if (item.days.includes(dayKey)) {
          scheduleItem = item;
          break;
        }
      }

      if (scheduleItem) {
        if (!scheduleItem.hours) {
          // 휴무일
          result.push({
            day: dayInfo.full,
            dayShort: dayInfo.ko,
            status: '휴무',
            time: '',
            isToday: new Date().getDay() === dayInfo.index
          });
        } else {
          // 영업일
          const { start, end } = scheduleItem.hours;
          result.push({
            day: dayInfo.full,
            dayShort: dayInfo.ko,
            status: '영업',
            time: `${start} ~ ${end}`,
            isToday: new Date().getDay() === dayInfo.index
          });
        }
      } else {
        // 스케줄에 없는 요일은 휴무로 처리
        result.push({
          day: dayInfo.full,
          dayShort: dayInfo.ko,
          status: '휴무',
          time: '',
          isToday: new Date().getDay() === dayInfo.index
        });
      }
    });

    return result;
  }

  // === 영업 상태 계산 ===
  
  /**
   * 현재 영업 상태 계산 (통합 메서드)
   */
  getCurrentOperatingStatus(scheduleData, targetDate = new Date()) {
    let scheduleJSON;
    let parseSuccess = false;
    
    // 입력 데이터가 없는 경우
    if (!scheduleData || scheduleData.trim() === '') {
      return { status: '정보 없음', time: '', nextChange: null };
    }
    
    // 다양한 포맷 지원 및 파싱 시도
    if (typeof scheduleData === 'string') {
      if (scheduleData.includes(':') && scheduleData.includes('~')) {
        // 기존 포맷: "월요일: 22:00 ~ 06:00"
        scheduleJSON = this.parseStringToJSON(scheduleData);
        parseSuccess = scheduleJSON?.schedule?.length > 0;
      } else if (scheduleData.includes(':') && (scheduleData.includes('-') || scheduleData.includes('/') || /[a-zA-Z]/.test(scheduleData))) {
        // 압축 포맷 또는 영어 포맷: "목/금/토 22:00-04:00", "Everyday 21:00-late"
        scheduleJSON = this.parseCompactToJSON(scheduleData);
        parseSuccess = scheduleJSON?.schedule?.length > 0;
      }
    } else if (typeof scheduleData === 'object' && scheduleData?.schedule) {
      scheduleJSON = scheduleData;
      parseSuccess = true;
    }
    
    // 파싱 실패한 경우 (포맷이 맞지 않음)
    if (!parseSuccess || !scheduleJSON?.schedule) {
      return { status: '정보 없음', time: '', nextChange: null };
    }
    
    const currentDay = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][targetDate.getDay()];
    const currentTime = targetDate.getHours() * 100 + targetDate.getMinutes();
    
    // 오늘의 운영시간 찾기
    const todaySchedule = scheduleJSON.schedule.find(item => 
      item.days.includes(currentDay)
    );
    
    // 파싱은 성공했지만 오늘이 스케줄에 없는 경우 (휴무일)
    if (!todaySchedule) {
      return { status: '휴무일', time: '', nextChange: null };
    }
    
    if (!todaySchedule.hours) {
      return { status: '휴무일', time: '', nextChange: null };
    }
    
    const { start, end } = todaySchedule.hours;
    const startTime = parseInt(start.replace(':', ''));
    
    // "late" 같은 특수 시간 처리 (계산용)
    let endTime;
    let isSpecialTime = false;
    if (this.isSpecialTime(end)) {
      isSpecialTime = true;
      const normalizedEnd = this.normalizeTime(end);
      endTime = parseInt(normalizedEnd.replace(':', ''));
    } else {
      endTime = parseInt(end.replace(':', ''));
    }
    
    const isOvernight = endTime < startTime;
    
    let isOpen = false;
    if (isOvernight) {
      isOpen = currentTime >= startTime || currentTime <= endTime;
    } else {
      isOpen = currentTime >= startTime && currentTime <= endTime;
    }
    
    // 표시용으로는 원본 문자열 사용 (late가 late로 표시됨)
    const timeDisplay = `${start} - ${end}`;
    
    if (isOpen) {
      return { 
        status: '영업중', 
        time: timeDisplay,
        nextChange: this.calculateNextChange(scheduleJSON, targetDate, 'close')
      };
    } else {
      // 영업 시작 전인지 종료 후인지 판단
      if (isOvernight) {
        if (currentTime < startTime && currentTime > endTime) {
          return { 
            status: '곧 오픈', 
            time: timeDisplay,
            nextChange: this.calculateNextChange(scheduleJSON, targetDate, 'open')
          };
        } else {
          return { 
            status: '영업종료', 
            time: timeDisplay,
            nextChange: this.calculateNextChange(scheduleJSON, targetDate, 'open')
          };
        }
      } else {
        if (currentTime < startTime) {
          return { 
            status: '곧 오픈', 
            time: timeDisplay,
            nextChange: this.calculateNextChange(scheduleJSON, targetDate, 'open')
          };
        } else {
          return { 
            status: '영업종료', 
            time: timeDisplay,
            nextChange: this.calculateNextChange(scheduleJSON, targetDate, 'open')
          };
        }
      }
    }
  }
  
  /**
   * 다음 영업시간 변경 시점 계산
   */
  calculateNextChange(scheduleJSON, currentDate, changeType) {
    // 구현 생략 (필요시 확장)
    return null;
  }

  // === 유틸리티 메서드들 ===
  
  /**
   * 시간 포맷팅 (HH:mm)
   */
  formatTime(timeStr) {
    if (!timeStr) return null;
    return timeStr.slice(0, 5);
  }
  
  /**
   * 24시간 형식을 12시간 형식으로 변환
   */
  to12HourFormat(time24) {
    if (!time24) return null;
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  }
  
  /**
   * 운영시간 요약 생성 (주중/주말 구분)
   */
  generateScheduleSummary(scheduleJSON) {
    if (!scheduleJSON?.schedule) return '';
    
    const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri'];
    const weekends = ['sat', 'sun'];
    
    let weekdayHours = null;
    let weekendHours = null;
    
    scheduleJSON.schedule.forEach(item => {
      const hasWeekday = item.days.some(day => weekdays.includes(day));
      const hasWeekend = item.days.some(day => weekends.includes(day));
      
      if (hasWeekday && item.hours) {
        weekdayHours = item.hours;
      }
      if (hasWeekend && item.hours) {
        weekendHours = item.hours;
      }
    });
    
    const parts = [];
    if (weekdayHours) {
      parts.push(`평일 ${weekdayHours.start}-${weekdayHours.end}`);
    }
    if (weekendHours && JSON.stringify(weekendHours) !== JSON.stringify(weekdayHours)) {
      parts.push(`주말 ${weekendHours.start}-${weekendHours.end}`);
    }
    
    return parts.join(' / ');
  }
  
  /**
   * 스케줄 유효성 검증
   */
  validateSchedule(scheduleJSON) {
    if (!scheduleJSON?.schedule || !Array.isArray(scheduleJSON.schedule)) {
      return { valid: false, error: '스케줄 데이터가 올바르지 않습니다.' };
    }
    
    const allDays = new Set();
    
    for (const item of scheduleJSON.schedule) {
      if (!Array.isArray(item.days) || item.days.length === 0) {
        return { valid: false, error: '요일 정보가 올바르지 않습니다.' };
      }
      
      // 중복 요일 체크
      for (const day of item.days) {
        if (allDays.has(day)) {
          return { valid: false, error: `중복된 요일이 있습니다: ${day}` };
        }
        allDays.add(day);
      }
      
      // 시간 형식 체크 (숫자 시간 형식 또는 특수 시간 표현 허용)
      if (item.hours) {
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        const specialTimeRegex = /^(late|early|midnight|noon)$/i;
        
        const isStartValid = timeRegex.test(item.hours.start) || specialTimeRegex.test(item.hours.start);
        const isEndValid = timeRegex.test(item.hours.end) || specialTimeRegex.test(item.hours.end);
        
        if (!isStartValid || !isEndValid) {
          return { valid: false, error: '시간 형식이 올바르지 않습니다.' };
        }
      }
    }
    
    return { valid: true };
  }
}

// 싱글톤 인스턴스
export const timeManager = new TimeScheduleManager();

// 편의 함수들 (기존 코드와의 호환성)
export const getTodayOperatingStatus = (operatingHours, targetDate) => {
  return timeManager.getCurrentOperatingStatus(operatingHours, targetDate);
};

export const formatOperatingHours = (weeklyHoursArray) => {
  // 임시로 기존 로직 유지 (호환성을 위해)
  return weeklyHoursArray.map(day => {
    if (day.closed) {
      return `${day.label}: 휴무`;
    } else if (day.start && day.end) {
      return `${day.label}: ${day.start} ~ ${day.end}`;
    } else {
      return null;
    }
  }).filter(line => line !== null).join('\n');
};

export const parseOperatingHours = (hoursString) => {
  // 임시로 기본 JSON 반환 (호환성을 위해)
  return timeManager.parseStringToJSON(hoursString);
};

export const formatTime = (timeStr) => {
  return timeManager.formatTime(timeStr);
}; 